import { EnvironmentProviders, importProvidersFrom } from '@angular/core';
import { AuthConfig, AuthModule } from '@auth0/auth0-angular';
import { provideAuth } from 'angular-auth-oidc-client';
import { AppConfig } from '@core/services/config/app-config.service';

/** The Auth0 SDK's client configuration for this deployment. */
export function auth0ClientConfig(config: AppConfig, origin: string): AuthConfig {
  return {
    domain: config.auth0CustomDomain,
    clientId: config.auth0ClientId,
    authorizationParams: {
      redirect_uri: origin,
      scope: 'openid profile email offline_access',
      audience: config.auth0ApiAudience,
    },
    useRefreshTokens: true,
    useRefreshTokensFallback: true,
    cacheLocation: 'localstorage',
    httpInterceptor: {
      allowedList: [
        ...config.protectedUrls.map((apiUrl) => `${apiUrl}/*`),
        {
          uri: `${config.apiOrigin}/api/account/email-lookup-p`,
          allowAnonymous: true,
        },
      ],
    },
  };
}

/**
 * The client-side auth library for the configured mode:
 * - BFF (cookie-based): none; cookies handle everything.
 * - OIDC (E2E / mock server): angular-auth-oidc-client.
 * - Auth0 (production, PR previews): the Auth0 Angular SDK.
 */
export function provideAuthLibrary(config: AppConfig, origin: string): EnvironmentProviders[] {
  if (config.authProvider === 'oidc') {
    return [
      provideAuth({
        config: {
          authority: config.oidcAuthority,
          redirectUrl: origin,
          postLogoutRedirectUri: origin,
          clientId: config.oidcClientId,
          scope: config.oidcScope ?? 'openid profile email offline_access',
          responseType: 'code',
          silentRenew: true,
          useRefreshToken: true,
          secureRoutes: [...config.protectedUrls],
          autoUserInfo: true,
        },
      }),
    ];
  }
  if (config.authProvider === 'bff') {
    return [];
  }
  // Configured here, with the providers, never later in an initializer (#4672): environment
  // initializers run before app initializers, and anything that reaches AUTH_PROVIDER (the
  // walkthrough launcher, through SignalR) builds the Auth0 client the moment it is injected.
  return [importProvidersFrom([AuthModule.forRoot(auth0ClientConfig(config, origin))])];
}
